// Topic / service-page registry. Single source of truth — used by the hub
// page, the per-topic pages, the homepage card grid, and the contact form
// topic dropdown.

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const img = (file) => `${BACKEND}/api/static/${file}`;

export const SERVICES = [
    {
        slug: "life-insurance",
        path: "/life-insurance",
        topic: "Life Insurance",
        kicker: "Family & Legacy",
        title: "Life Insurance",
        seo: {
            title: "Term & Whole Life Insurance Policies | Endurance Casualty",
            description:
                "Protect your family's financial future with term or whole life insurance. Compare policies and rates with Endurance Casualty.",
            h1: "Life Insurance Policies",
        },
        intro:
            "Endurance Casualty provides helpful general information and a simple way to begin a conversation about life insurance. Term, whole life, universal, and final-expense conversations are common starting points. Products, services, materials, timing, follow-up, and next steps are subject to availability, which may change.",
        whyContact: [
            "You're starting a family or buying a home and want a basic safety net.",
            "You'd like to understand the difference between term and permanent options.",
            "You're reviewing existing coverage and want a second opinion.",
            "You're planning around business succession, key-person protection, or buy-sell agreements.",
        ],
        commonTopics: [
            "Term life",
            "Whole life",
            "Universal life",
            "Final-expense",
            "Beneficiary review",
        ],
        related: ["health-insurance", "supplemental-benefits", "personal-insurance"],
        image: img("life.png"),
    },
    {
        slug: "supplemental-benefits",
        path: "/supplemental-benefits",
        topic: "Supplemental Benefits",
        kicker: "Voluntary & Worksite",
        title: "Supplemental Benefits",
        intro:
            "Endurance Casualty provides helpful general information and a simple way to begin a conversation about supplemental and voluntary benefits — coverages employees and individuals layer alongside primary medical or income protection. Products, services, materials, timing, follow-up, and next steps are subject to availability, which may change.",
        whyContact: [
            "You're an employer interested in offering voluntary worksite benefits.",
            "You're an individual wanting to fill gaps in existing coverage.",
            "You're considering accident, critical-illness, hospital, or disability conversations.",
            "You'd like to understand how supplemental benefits coordinate with primary coverage.",
        ],
        commonTopics: [
            "Accident",
            "Critical illness",
            "Hospital indemnity",
            "Short / long-term disability",
            "Voluntary worksite plans",
        ],
        related: ["health-insurance", "life-insurance", "business-insurance"],
        image: img("supplemental.png"),
    },
    {
        slug: "health-insurance",
        path: "/health-insurance",
        topic: "Health Insurance",
        kicker: "Wellness & Care",
        title: "Health Insurance",
        seo: {
            title: "Individual, Family & Group Health Insurance | Endurance Casualty",
            description:
                "Explore health insurance plans for individuals, families, and businesses. Compare coverage options with an experienced multi-state agency.",
            h1: "Health Insurance Coverage Options",
        },
        intro:
            "Endurance Casualty provides helpful general information and a simple way to begin a conversation about health insurance — for individuals, families, the self-employed, and Medicare-eligible adults. Products, services, materials, timing, follow-up, and next steps are subject to availability, which may change.",
        whyContact: [
            "You're between jobs or losing employer coverage and weighing options.",
            "You're self-employed or run a small business and want individual or family coverage.",
            "You'd like to understand Marketplace, supplemental, dental, or vision conversations.",
            "You're approaching Medicare eligibility and want a clear walkthrough.",
            "You wish to look at alternate health structures to reduce cost or change coverage.",
        ],
        commonTopics: [
            "Individual & family",
            "Self-employed",
            "Marketplace conversations",
            "Dental & vision",
            "Medicare-eligible discussions",
        ],
        related: ["supplemental-benefits", "life-insurance", "personal-insurance"],
        image: img("health.png"),
    },
    {
        slug: "personal-insurance",
        path: "/personal-insurance",
        topic: "Personal Insurance",
        kicker: "Home & Household",
        title: "Personal Insurance",
        intro:
            "Endurance Casualty provides helpful general information and a simple way to begin a conversation about personal and household insurance. Homeowners, renters, condo, dwelling-fire, umbrella, and personal-property conversations are common starting points. Products, services, materials, timing, follow-up, and next steps are subject to availability, which may change.",
        whyContact: [
            "You're buying or refinancing a home and need a starting policy review.",
            "You rent and want to understand renters coverage at a basic level.",
            "You own a condo or vacation home and want a tailored conversation.",
            "You're considering a personal umbrella or higher liability protection.",
        ],
        commonTopics: [
            "Homeowners (HO)",
            "Renters",
            "Condo (HO-6)",
            "Dwelling fire (DP)",
            "Personal umbrella",
        ],
        related: ["extreme-weather", "life-insurance", "business-insurance"],
        image: img("property.png"),
    },
    {
        slug: "business-insurance",
        path: "/business-insurance",
        topic: "Business Insurance",
        kicker: "Owners & Operators",
        title: "Business Insurance",
        seo: {
            title: "Small Business & Commercial Insurance | Endurance Casualty",
            description:
                "Compare small business and commercial insurance options across 7 states. Workers' comp, general liability, BOP, commercial auto, cyber, and D&O.",
            h1: "Small Business & Commercial Insurance",
        },
        intro:
            "Endurance Casualty provides helpful general information and a simple way to begin a conversation about business insurance for small and mid-sized operators. Business owner's policies (BOP), general liability, commercial property, and workers' compensation are common starting points. Products, services, materials, timing, follow-up, and next steps are subject to availability, which may change.",
        whyContact: [
            "You're forming a new business and want a basic protection conversation.",
            "You're a contractor, consultant, or service operator weighing liability options.",
            "You manage payroll and want to discuss workers' compensation requirements.",
            "You'd like to understand BOP versus stand-alone property and liability.",
            "You need specialized insurance for particular risk management.",
        ],
        commonTopics: [
            "BOP",
            "General liability",
            "Commercial property",
            "Workers' compensation",
            "Hired & non-owned auto",
        ],
        related: ["commercial-lines", "cyber-insurance", "supplemental-benefits"],
        image: img("business.png"),
    },
    {
        slug: "commercial-lines",
        path: "/commercial-lines",
        topic: "Commercial Lines",
        kicker: "Specialty & General Agency",
        title: "Commercial Lines & General Agency",
        seo: {
            title: "Commercial Lines & General Agency | Endurance Casualty",
            description:
                "Comprehensive commercial lines coverage for complex business risks. Access top-rated markets through our general agency.",
            h1: "Commercial Lines Insurance",
        },
        intro:
            "Endurance Casualty provides helpful general information and a simple way to begin a conversation about commercial lines through our brokerage and general-agency relationships. D&O, E&O, surety and fidelity bonds, CGL, BOP, commercial property, workers' compensation, and specialty commercial discussions are common starting points. Products, services, materials, timing, follow-up, and next steps are subject to availability, which may change.",
        whyContact: [
            "You're a director, officer, or board member exploring D&O conversations.",
            "You provide professional services and want to discuss E&O.",
            "You need to discuss surety or fidelity bond requirements.",
            "You're scaling beyond a standard BOP and exploring specialty commercial options.",
        ],
        commonTopics: [
            "D&O",
            "E&O",
            "Surety & fidelity bonds",
            "CGL",
            "BOP & commercial property",
            "Workers' comp",
            "Specialty commercial",
        ],
        related: ["business-insurance", "cyber-insurance", "housing-community-services"],
        image: img("commercial.png"),
    },
    {
        slug: "cyber-insurance",
        path: "/cyber-insurance",
        topic: "Cyber Insurance",
        kicker: "Data & Digital Risk",
        title: "Cyber Insurance",
        seo: {
            title: "Cyber Liability Insurance for Businesses | Endurance Casualty",
            description:
                "Protect your business from data breaches, ransomware, and cyberattacks with tailored cyber liability insurance.",
            h1: "Cyber Liability Insurance",
        },
        intro:
            "Endurance Casualty provides helpful general information and a simple way to begin a conversation about cyber risk, data, technology, systems, vendors, online operations, and digital business exposure. Products, services, materials, timing, follow-up, and next steps are subject to availability, which may change.",
        whyContact: [
            "You handle customer data, payment information, or health information.",
            "You operate online, accept payments digitally, or run a SaaS or e-commerce business.",
            "You depend on third-party vendors, cloud services, or remote teams.",
            "You'd like a general conversation about cyber-related coverage options.",
        ],
        commonTopics: [
            "First-party cyber",
            "Third-party / liability",
            "Vendor & supply-chain risk",
            "Privacy & data exposure",
            "Technology operations",
        ],
        related: ["business-insurance", "commercial-lines", "supplemental-benefits"],
        image: img("cyber.png"),
    },
    {
        slug: "extreme-weather",
        path: "/extreme-weather",
        topic: "Extreme Weather",
        kicker: "Storm, Wind, Water & Fire",
        title: "Extreme Weather",
        intro:
            "Endurance Casualty provides helpful general information and a simple way to begin a conversation about flood, hurricane, wind, hail, tornado, wildfire, earthquake, severe-storm, coastal, and waterfront risks. Products, services, materials, timing, follow-up, and next steps are subject to availability, which may change.",
        whyContact: [
            "You own coastal or waterfront property and want a flood / hurricane conversation.",
            "You live in tornado, hail, or severe-storm corridors.",
            "You're in a wildfire-prone area and want a general conversation.",
            "You'd like to understand how separate policies, endorsements, or deductibles may apply.",
        ],
        commonTopics: [
            "Flood",
            "Hurricane / named storm",
            "Wind & hail",
            "Tornado",
            "Earthquake",
            "Wildfire",
            "Coastal & waterfront",
        ],
        related: ["personal-insurance", "business-insurance", "commercial-lines"],
        image: img("florida.png"),
        secondaryImage: img("texas.png"),
    },
    {
        slug: "housing-community-services",
        path: "/housing-community-services",
        topic: "Housing & Community Services",
        kicker: "Community Services & Operators",
        title: "Housing & Community Services",
        intro:
            "Endurance Casualty provides helpful general information and a simple way to begin a conversation with organizations, property owners, operators, contractors, nonprofits, and service providers connected to shelters, transitional and supportive housing, outreach, community services, or related operations. Products, services, materials, timing, follow-up, and next steps are subject to availability, which may change.",
        whyContact: [
            "You're a homeowner, dwelling owner, or multifamily owner seeking alternative programs and/or a review of coverage.",
            "You operate or fund a shelter, transitional housing, or supportive-services program.",
            "You're a contractor or vendor that serves community-services organizations.",
            "You manage property used for outreach, day services, or supportive housing.",
            "You'd like a respectful, practical conversation about insurance topics connected to your operations.",
        ],
        commonTopics: [
            "Nonprofit & community-services operations",
            "Shelter & transitional housing",
            "Outreach & day-services programs",
            "Property used for supportive services",
            "Contractors serving community programs",
        ],
        related: ["commercial-lines", "business-insurance", "cyber-insurance"],
        image: img("housing-community.png"),
    },
];

export const SERVICE_BY_SLUG = Object.fromEntries(
    SERVICES.map((s) => [s.slug, s])
);

// Topic options for the contact-form / contact-dialog topic selector
export const CONTACT_TOPICS = [
    "Life Insurance",
    "Supplemental Benefits",
    "Health Insurance",
    "Personal Insurance",
    "Business Insurance",
    "Commercial Lines",
    "Cyber Insurance",
    "Extreme Weather",
    "Housing & Community Services",
    "Employer Benefits",
    "General Question",
];
